// src/pages/RegisterPage.js
// Fase 5 + Fase 12b: Dropdown dinámico + campos de registro configurables por proyecto
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { authService, API_BASE_URL } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { buscarCampo, listaValores, alternarValor, cajaCasillas } from '../utils/registroCampos';
import axios from 'axios';
import VerContrasena from '../components/VerContrasena';

const Container = styled.div`
  max-width: 420px;
  margin: 3rem auto;
  padding: 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  text-align: center;
`;

const Title = styled.h2`
  margin-bottom: 1.5rem;
  color: #2b4361;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  margin-bottom: 1rem;
  border-radius: 6px;
  border: 1px solid #ccc;
  background-color: #eef4ff;
  font-size: 0.95rem;
  box-sizing: border-box;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  margin-bottom: 1rem;
  border-radius: 6px;
  border: 1px solid #ccc;
  background-color: #eef4ff;
  font-size: 0.95rem;
  box-sizing: border-box;
  color: ${props => props.value ? '#2b4361' : '#9ca3af'};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #0891B2;
    box-shadow: 0 0 0 2px rgba(8,145,178,0.2);
  }
`;

const SelectLabel = styled.label`
  display: block;
  text-align: left;
  font-size: 0.9rem;
  color: #2b4361;
  font-weight: 600;
  margin-bottom: 0.4rem;
`;

const Button = styled.button`
  width: 100%;
  background-color: #dc2626;
  color: white;
  padding: 0.75rem;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: #b91c1c;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMessage = styled.p`
  color: #e74c3c;
  font-size: 0.9rem;
  margin-top: -0.5rem;
  margin-bottom: 1rem;
`;

const PhoneRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const CODIGOS_PAIS = [
  { code: '+57', label: '+57 CO' },
  { code: '+52', label: '+52 MX' },
  { code: '+51', label: '+51 PE' },
  { code: '+593', label: '+593 EC' },
  { code: '+56', label: '+56 CL' },
  { code: '+54', label: '+54 AR' },
  { code: '+1', label: '+1 US' },
  { code: '+34', label: '+34 ES' },
  { code: '+55', label: '+55 BR' },
  { code: '+58', label: '+58 VE' },
  { code: '+591', label: '+591 BO' },
  { code: '+595', label: '+595 PY' },
  { code: '+598', label: '+598 UY' },
  { code: '+506', label: '+506 CR' },
  { code: '+507', label: '+507 PA' },
];

const RegisterPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('');
  const [error, setError] = useState('');
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);

  // Campos dinámicos
  const [registroConfig, setRegistroConfig] = useState(null);
  const [convenios, setConvenios] = useState([]);
  const [convenioId, setConvenioId] = useState('');
  const [telefono, setTelefono] = useState('');
  const [codigoPais, setCodigoPais] = useState('+57');
  const [especialidad, setEspecialidad] = useState('');
  const [pais, setPais] = useState('');
  const [dni, setDni] = useState('');
  const [subrol, setSubrol] = useState('');
  const [subrolesCatalogo, setSubrolesCatalogo] = useState([]);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/public-groups.php`);
        if (res.data?.success && res.data.groups.length > 0) {
          setGroups(res.data.groups);
          setRole(res.data.groups[0].name);
        }
        if (res.data?.registro_config) {
          setRegistroConfig(res.data.registro_config);
        }
        if (res.data?.convenios) {
          setConvenios(res.data.convenios);
        }
        if (Array.isArray(res.data?.subroles)) {
          setSubrolesCatalogo(res.data.subroles);
        }
      } catch (err) {
        console.error('Error cargando grupos:', err);
        setGroups([{ name: 'estudiante', description: 'Estudiante' }]);
        setRole('estudiante');
      } finally {
        setLoadingGroups(false);
      }
    };
    fetchGroups();
  }, []);

  // Lee los campos con el normalizador para soportar el formato viejo (objeto)
  // y el nuevo (array ordenado) — ver utils/registroCampos.js.
  const campoVisible = (campo) => !!buscarCampo(registroConfig, campo)?.visible;
  const campoRequerido = (campo) => !!buscarCampo(registroConfig, campo)?.requerido;
  // La etiqueta puede venir como string o como objeto i18n {es,en,pt}.
  const campoEtiqueta = (campo) => {
    const raw = buscarCampo(registroConfig, campo)?.etiqueta;
    if (typeof raw === 'string') return raw;
    if (raw && typeof raw === 'object') return raw.es || Object.values(raw).find(Boolean) || '';
    return '';
  };
  // Tipo de control del campo (hoy solo lo usa `especialidad`: 'lista' | 'texto').
  const campoTipo = (campo) => buscarCampo(registroConfig, campo)?.tipo;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!role) {
      setError('Selecciona un grupo de contenido');
      return;
    }

    // Validar campos requeridos dinámicos. Un campo que no se llegó a dibujar
    // (convenio/país/especialidad/perfil sin catálogo) no puede ser obligatorio:
    // si no, el registro queda bloqueado por algo que no se ve.
    const especialidadDibujada = campoTipo('especialidad') === 'texto'
      || !!registroConfig?.especialidades?.length;
    if (campoRequerido('convenio') && convenios.length && !convenioId) {
      setError('Selecciona un convenio');
      return;
    }
    if (campoRequerido('telefono') && !telefono.trim()) {
      setError('El teléfono es obligatorio');
      return;
    }
    if (campoRequerido('especialidad') && especialidadDibujada && !especialidad.trim()) {
      setError(`${campoEtiqueta('especialidad') || 'La especialidad'} es obligatoria`);
      return;
    }
    if (campoRequerido('pais') && registroConfig?.paises?.length && !pais) {
      setError('El país es obligatorio');
      return;
    }
    if (campoRequerido('dni') && !dni.trim()) {
      setError(`${campoEtiqueta('dni') || 'DNI'} es obligatorio`);
      return;
    }
    if (campoRequerido('subrol') && subrolesCatalogo.length && !subrol) {
      setError(`${campoEtiqueta('subrol') || 'El perfil'} es obligatorio`);
      return;
    }

    try {
      const payload = { nombre, email, password, role };
      if (campoVisible('convenio') && convenioId) payload.convenio_id = parseInt(convenioId);
      if (campoVisible('telefono') && telefono) {
        payload.telefono = telefono;
        payload.codigo_pais = codigoPais;
      }
      if (campoVisible('especialidad') && especialidad.trim()) payload.especialidad = especialidad.trim();
      if (campoVisible('pais') && pais) payload.pais = pais;
      if (campoVisible('dni') && dni) payload.dni = dni;
      if (campoVisible('subrol') && subrol) payload.subrol = subrol;

      const response = await authService.register(payload);

      if (!response || !response.data) {
        throw new Error('Respuesta de registro inválida');
      }

      const { token, user } = response.data;

      if (!user || user.id === null || user.id === undefined) {
        if (user && user.email) {
          let hash = 0;
          for (let i = 0; i < user.email.length; i++) {
            hash = ((hash << 5) - hash) + user.email.charCodeAt(i);
            hash |= 0;
          }
          const userWithId = { ...user, id: Math.abs(hash) };
          login({ token, user: userWithId });
          navigate('/contenidos');
        } else {
          throw new Error('No se pudo generar ID para el usuario');
        }
      } else {
        if (typeof user.id === 'string') {
          user.id = parseInt(user.id, 10);
        }
        login({ token, user });
        navigate('/contenidos');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Error al registrar usuario');
    }
  };

  return (
    <Container>
      <Title>Registrarse</Title>
      <form onSubmit={handleSubmit}>
        <Input
          type="text"
          placeholder="Nombre completo"
          value={nombre}
          onChange={e => setNombre(e.target.value)}
          required
        />
        <Input
          type="email"
          placeholder="Correo electrónico"
          value={email}
          onChange={e => setEmail(e.target.value)}
          required
        />
        <VerContrasena flotante>
          {tipo => (
            <Input
              type={tipo}
              placeholder="Contraseña"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
            />
          )}
        </VerContrasena>

        <div style={{ marginBottom: '1rem', textAlign: 'left' }}>
          <SelectLabel>Grupo de contenido:</SelectLabel>
          <Select
            value={role}
            onChange={e => setRole(e.target.value)}
            disabled={loadingGroups}
          >
            {loadingGroups ? (
              <option value="">Cargando grupos...</option>
            ) : groups.length === 0 ? (
              <option value="">No hay grupos disponibles</option>
            ) : (
              groups.map(g => (
                <option key={g.name} value={g.name}>
                  {g.name.charAt(0).toUpperCase() + g.name.slice(1)}
                  {g.description ? ` — ${g.description}` : ''}
                </option>
              ))
            )}
          </Select>
        </div>

        {/* === Campos dinámicos === */}

        {campoVisible('convenio') && convenios.length > 0 && (
          <div style={{ marginBottom: '1rem', textAlign: 'left' }}>
            <SelectLabel>
              Convenio{campoRequerido('convenio') ? ' *' : ''}:
            </SelectLabel>
            <Select
              value={convenioId}
              onChange={e => setConvenioId(e.target.value)}
            >
              <option value="">Selecciona un convenio</option>
              {convenios.map(c => (
                <option key={c.id} value={c.id}>
                  {c.nombre}{c.institucion ? ` — ${c.institucion}` : ''}
                </option>
              ))}
            </Select>
          </div>
        )}

        {campoVisible('telefono') && (
          <div style={{ marginBottom: '1rem', textAlign: 'left' }}>
            <SelectLabel>
              Teléfono{campoRequerido('telefono') ? ' *' : ''}:
            </SelectLabel>
            <PhoneRow>
              <Select
                value={codigoPais}
                onChange={e => setCodigoPais(e.target.value)}
                style={{ width: '110px', flex: 'none' }}
              >
                {CODIGOS_PAIS.map(cp => (
                  <option key={cp.code} value={cp.code}>{cp.label}</option>
                ))}
              </Select>
              <Input
                type="tel"
                placeholder="Número de teléfono"
                value={telefono}
                onChange={e => setTelefono(e.target.value)}
                style={{ marginBottom: 0, flex: 1 }}
              />
            </PhoneRow>
          </div>
        )}

        {/* Especialidad: el proyecto elige el tipo de control desde el admin.
            'texto' = campo libre; 'checkbox' = casillas (varias opciones);
            cualquier otro valor (o ninguno) = lista desplegable, como siempre. */}
        {campoVisible('especialidad')
          && (campoTipo('especialidad') === 'texto' || registroConfig?.especialidades?.length > 0) && (
          <div style={{ marginBottom: '1rem', textAlign: 'left' }}>
            <SelectLabel>
              {campoEtiqueta('especialidad') || 'Especialidad'}{campoRequerido('especialidad') ? ' *' : ''}:
            </SelectLabel>
            {campoTipo('especialidad') === 'texto' ? (
              <Input
                type="text"
                placeholder="Escribe tu especialidad"
                value={especialidad}
                onChange={e => setEspecialidad(e.target.value)}
                style={{ marginBottom: 0 }}
              />
            ) : campoTipo('especialidad') === 'checkbox' ? (
              <div style={cajaCasillas(registroConfig.especialidades.length)}>
                {registroConfig.especialidades.map(e => (
                  <label key={e} style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', fontSize: '0.9rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={listaValores(especialidad).includes(e)}
                      onChange={() => setEspecialidad(prev => alternarValor(prev, e))}
                    />
                    {e}
                  </label>
                ))}
              </div>
            ) : (
              <Select
                value={especialidad}
                onChange={e => setEspecialidad(e.target.value)}
              >
                <option value="">Selecciona especialidad</option>
                {registroConfig.especialidades.map(e => (
                  <option key={e} value={e}>{e}</option>
                ))}
              </Select>
            )}
          </div>
        )}

        {campoVisible('pais') && registroConfig?.paises?.length > 0 && (
          <div style={{ marginBottom: '1rem', textAlign: 'left' }}>
            <SelectLabel>
              País{campoRequerido('pais') ? ' *' : ''}:
            </SelectLabel>
            <Select
              value={pais}
              onChange={e => setPais(e.target.value)}
            >
              <option value="">Selecciona país</option>
              {registroConfig.paises.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </Select>
          </div>
        )}

        {campoVisible('dni') && (
          <div style={{ marginBottom: '1rem', textAlign: 'left' }}>
            <SelectLabel>
              {campoEtiqueta('dni') || 'DNI'}{campoRequerido('dni') ? ' *' : ''}:
            </SelectLabel>
            <Input
              type="text"
              placeholder="Número de documento"
              value={dni}
              onChange={e => setDni(e.target.value)}
              style={{ marginBottom: 0 }}
            />
          </div>
        )}

        {campoVisible('subrol') && subrolesCatalogo.length > 0 && (
          <div style={{ marginBottom: '1rem', textAlign: 'left' }}>
            <SelectLabel>
              {campoEtiqueta('subrol') || 'Perfil'}{campoRequerido('subrol') ? ' *' : ''}:
            </SelectLabel>
            <Select value={subrol} onChange={e => setSubrol(e.target.value)}>
              <option value="">Selecciona tu perfil</option>
              {subrolesCatalogo.map(sr => (
                <option key={sr.key} value={sr.key}>{sr.label || sr.key}</option>
              ))}
            </Select>
          </div>
        )}

        {error && <ErrorMessage>{error}</ErrorMessage>}
        <Button type="submit" disabled={loadingGroups || !role}>
          Crear cuenta
        </Button>
      </form>
    </Container>
  );
};

export default RegisterPage;
